import type {
  AddAdminOrderScriptVideoSlotResult,
} from "@/features/marketplace/api/marketplace.api";
import type {
  OrderLineVideo,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";

/** Matches the API presentation: an empty slot carries no fulfillment status. */
export function emptyOrderLineVideoFromSlot(
  slot: AddAdminOrderScriptVideoSlotResult,
): OrderLineVideo {
  return {
    id: slot.id,
    position: slot.position,
    label: slot.label,
    preflightUploadId: null,
    scriptReviewId: null,
    preflightUrl: null,
    reviewStatus: "IN_REVIEW",
    revisionNote: null,
    isEmpty: true,
    fulfillmentStatus: null,
  };
}

function patchScriptVideos(
  script: OrderScriptRow,
  updater: (videos: OrderLineVideo[]) => OrderLineVideo[],
): OrderScriptRow {
  const videos = updater([...(script.videos ?? [])]).sort(
    (left, right) => left.position - right.position,
  );
  return { ...script, videos };
}

type OrderWithScripts = {
  id: string;
  scripts: OrderScriptRow[];
};

export function patchOrderAfterVideoSlotAdded<T extends OrderWithScripts>(
  order: T,
  orderScriptId: string,
  slot: AddAdminOrderScriptVideoSlotResult,
): T {
  return {
    ...order,
    scripts: order.scripts.map((script) => {
      if (script.id !== orderScriptId) return script;
      const video = emptyOrderLineVideoFromSlot(slot);
      if (script.videos?.some((row) => row.id === video.id)) {
        return script;
      }
      return patchScriptVideos(script, (videos) => [...videos, video]);
    }),
  };
}

export function patchOrderAfterVideoSlotRemoved<T extends OrderWithScripts>(
  order: T,
  orderScriptId: string,
  deliverableId: string,
): T {
  return {
    ...order,
    scripts: order.scripts.map((script) => {
      if (script.id !== orderScriptId) return script;
      return patchScriptVideos(script, (videos) =>
        videos.filter((video) => video.id !== deliverableId),
      );
    }),
  };
}
